import React, { useCallback, useMemo, useState } from 'react'
import { AnimatePresence, motion } from 'framer-motion'
import RubroQuotationDatesStrip from '../../../RubroQuotationDatesStrip/RubroQuotationDatesStrip.tsx'
import RubroQuotationTimeline from './RubroQuotationTimeline.tsx'
import cardStyles from './ProjectAutofillCard.module.scss'
import editorStyles from './RubroQuotationWindowSection.module.scss'
import {
  buildQuotationTimelineScale,
  computeRecommendedQuotationWindow,
  enforceMinQuotationWindowDates,
  quotationWindowMatches,
  type RecommendedQuotationWindow
} from './rubroQuotationTimelineUtils.ts'

export type RubroQuotationWindowLayout = 'preview' | 'editor'

type RubroDatesPatch = {
  startDate: string
  endDate: string
  datesInheritedFromProject: boolean
}

type Props = {
  sectionId: string
  projectStartDate: string
  projectEndDate: string
  rubroStartDate: string
  rubroEndDate: string
  recommendedQuotationWindow?: RecommendedQuotationWindow
  draftLocked: boolean
  today: string
  disabledTitle?: string
  layout?: RubroQuotationWindowLayout
  clampIsoDateToMax: (iso: string, maxIso: string) => string
  onRubroDatesChange: (patch: RubroDatesPatch) => void
}

const panelMotion = {
  initial: { opacity: 0, height: 0 },
  animate: { opacity: 1, height: 'auto' },
  exit: { opacity: 0, height: 0 },
  transition: { duration: 0.3, ease: 'easeInOut' }
}

const RubroQuotationWindowSection: React.FC<Props> = ({
  sectionId,
  projectStartDate,
  projectEndDate,
  rubroStartDate,
  rubroEndDate,
  recommendedQuotationWindow,
  draftLocked,
  today,
  disabledTitle,
  layout = 'preview',
  clampIsoDateToMax,
  onRubroDatesChange
}) => {
  const isEditorLayout = layout === 'editor'
  const [showTimelineEditor, setShowTimelineEditor] = useState(isEditorLayout)
  const [showExactDates, setShowExactDates] = useState(false)

  const timelineScale = useMemo(
    () => buildQuotationTimelineScale(projectStartDate, projectEndDate),
    [projectStartDate, projectEndDate]
  )

  const effectiveRecommended = useMemo(() => {
    if (recommendedQuotationWindow) return recommendedQuotationWindow
    return computeRecommendedQuotationWindow(projectStartDate, projectEndDate) ?? undefined
  }, [recommendedQuotationWindow, projectStartDate, projectEndDate])

  const showRestoreSuggestion =
    !draftLocked &&
    !!effectiveRecommended &&
    !quotationWindowMatches(
      { startDate: rubroStartDate, endDate: rubroEndDate },
      effectiveRecommended
    )

  const canUseTimelineSlider = !!timelineScale && !draftLocked
  const timelineVisible = canUseTimelineSlider && (isEditorLayout || showTimelineEditor)

  const canUseProjectCalendar =
    !draftLocked && !!projectStartDate?.trim() && !!projectEndDate?.trim() && !!effectiveRecommended

  const commitWindowDates = useCallback(
    (startDate: string, endDate: string, datesInheritedFromProject: boolean) => {
      let nextStart = startDate
      let nextEnd = endDate
      if (projectEndDate && nextStart) {
        nextStart = clampIsoDateToMax(nextStart, projectEndDate)
      }
      if (projectEndDate && nextEnd) {
        nextEnd = clampIsoDateToMax(nextEnd, projectEndDate)
      }
      if (projectStartDate && nextStart && nextStart < projectStartDate) {
        nextStart = projectStartDate
      }
      if (nextEnd && nextStart && nextEnd < nextStart) {
        nextEnd = nextStart
      }
      const enforced = enforceMinQuotationWindowDates(
        projectStartDate,
        projectEndDate,
        nextStart,
        nextEnd
      )
      onRubroDatesChange({
        startDate: enforced.startDate,
        endDate: enforced.endDate,
        datesInheritedFromProject
      })
    },
    [clampIsoDateToMax, onRubroDatesChange, projectEndDate, projectStartDate]
  )

  const applyWindowDates = useCallback(
    (startDate: string, endDate: string) => {
      commitWindowDates(startDate, endDate, false)
    },
    [commitWindowDates]
  )

  const restoreSuggestedWindow = useCallback(() => {
    if (!effectiveRecommended) return
    applyWindowDates(effectiveRecommended.startDate, effectiveRecommended.endDate)
  }, [applyWindowDates, effectiveRecommended])

  const exactDateFields = (
    <>
      <label className={cardStyles.projectAutofillRubroDateLabel}>
        <span>Apertura del envío de presupuestos</span>
        <input
          type="date"
          value={rubroStartDate || ''}
          min={projectStartDate || today}
          max={projectEndDate || undefined}
          title={draftLocked ? disabledTitle : undefined}
          onChange={(e) => {
            let nextStart = e.target.value
            let nextEnd = rubroEndDate
            if (nextEnd && nextStart && nextEnd < nextStart) {
              nextEnd = nextStart
            }
            applyWindowDates(nextStart, nextEnd)
          }}
          disabled={draftLocked}
        />
      </label>
      <label className={cardStyles.projectAutofillRubroDateLabel}>
        <span>Cierre del envío de presupuestos</span>
        <input
          type="date"
          value={rubroEndDate || ''}
          min={rubroStartDate || projectStartDate || today}
          max={projectEndDate || undefined}
          title={draftLocked ? disabledTitle : undefined}
          onChange={(e) => {
            applyWindowDates(rubroStartDate, e.target.value)
          }}
          disabled={draftLocked}
        />
      </label>
    </>
  )

  const timelineEditorPanel = (
    <div className={editorStyles.editorPanel}>
      <div className={editorStyles.timelineHost}>
        <RubroQuotationTimeline
          projectStartDate={projectStartDate}
          projectEndDate={projectEndDate}
          windowStartDate={rubroStartDate}
          windowEndDate={rubroEndDate}
          disabled={draftLocked}
          onWindowDatesChange={applyWindowDates}
        />
      </div>
      <button
        type="button"
        className={cardStyles.projectAutofillQuotationExactDatesToggle}
        onClick={() => setShowExactDates((v) => !v)}
        aria-expanded={showExactDates}
      >
        {showExactDates ? 'Ocultar fechas exactas' : 'Editar fechas exactas'}
      </button>
      {showExactDates ? (
        isEditorLayout ? (
          <div className={editorStyles.editorPanel}>{exactDateFields}</div>
        ) : (
          <AnimatePresence initial={false}>
            <motion.div className={editorStyles.editorPanel} {...panelMotion}>
              {exactDateFields}
            </motion.div>
          </AnimatePresence>
        )
      ) : null}
    </div>
  )

  return (
    <section
      className={`${cardStyles.projectAutofillRubroDetailBlock} ${cardStyles.projectAutofillRubroDates} ${editorStyles.root}`}
      aria-labelledby={sectionId}
    >
      <div className={cardStyles.projectAutofillRubroDatesHead}>
        <h3 id={sectionId} className={cardStyles.projectAutofillRubroDetailBlockHeading}>
          Ventana de cotización
        </h3>
        {!isEditorLayout && showRestoreSuggestion ? (
          <button
            type="button"
            className={cardStyles.projectAutofillQuotationRestoreBtn}
            onClick={restoreSuggestedWindow}
          >
            Restaurar sugerencia
          </button>
        ) : null}
      </div>

      {isEditorLayout && showRestoreSuggestion && canUseProjectCalendar ? (
        <div className={editorStyles.suggestionRow}>
          <p className={editorStyles.suggestionHint}>
            Las fechas de este rubro difieren de la ventana sugerida según la publicación del
            proyecto.
          </p>
          <button
            type="button"
            className={editorStyles.suggestionRestoreBtn}
            onClick={restoreSuggestedWindow}
          >
            Restaurar ventana sugerida
          </button>
        </div>
      ) : null}

      {!isEditorLayout ? (
        <>
          <RubroQuotationDatesStrip
            windowStartDate={rubroStartDate}
            windowEndDate={rubroEndDate}
            startLabel="Inicio"
            endLabel="Fin"
          />
          <p className={editorStyles.previewLead}>
            Así verán los cotizantes las fechas de este rubro cuando publiques la obra.
          </p>
          {canUseTimelineSlider ? (
            <button
              type="button"
              className={editorStyles.editorToggle}
              onClick={() => setShowTimelineEditor((v) => !v)}
              aria-expanded={showTimelineEditor}
            >
              {showTimelineEditor ? 'Ocultar calendario de ajuste' : 'Ajustar ventana en el calendario'}
            </button>
          ) : null}
        </>
      ) : null}

      {timelineVisible ? (
        isEditorLayout ? (
          timelineEditorPanel
        ) : (
          <AnimatePresence initial={false}>
            <motion.div {...panelMotion}>{timelineEditorPanel}</motion.div>
          </AnimatePresence>
        )
      ) : null}

      {!canUseTimelineSlider ? exactDateFields : null}
    </section>
  )
}

export default RubroQuotationWindowSection
